import {
  FiActivity, FiBriefcase, FiCalendar, FiCpu, FiHardDrive, FiImage, FiLink, FiMail,
  FiMapPin, FiPlayCircle, FiSettings, FiShield, FiUserCheck,
} from 'react-icons/fi';

/**
 * One hue per settings group — and that hue is spent in exactly one place: the
 * 6px dot in the sidebar, where it earns its keep as a scanning aid.
 *
 * Everything else on this screen is neutral on purpose. Colour here means
 * STATE, not category: amber = not configured, violet = overridden, primary =
 * unsaved, red = destructive. A page that tints every group header, chip and
 * count badge has no colour left to say "this one needs you".
 */
export const GROUP_META = {
  general:            { dot: '#64748b', icon: FiSettings },
  ui:                 { dot: '#f43f5e', icon: FiImage },
  email:              { dot: '#0891b2', icon: FiMail },
  integration:        { dot: '#059669', icon: FiLink },
  account_management: { dot: '#4f46e5', icon: FiUserCheck },
  booking:            { dot: '#0d9488', icon: FiCalendar },
  crm:                { dot: '#ea580c', icon: FiBriefcase },
  ai:                 { dot: '#8b5cf6', icon: FiCpu },
  storage:            { dot: '#d97706', icon: FiHardDrive },
  background:         { dot: '#7c3aed', icon: FiPlayCircle },
  monitoring:         { dot: '#f43f5e', icon: FiActivity },
  security:           { dot: '#dc2626', icon: FiShield },
  geo:                { dot: '#0284c7', icon: FiMapPin },
};

export const groupMeta = (key) => GROUP_META[key] || { dot: '#94a3b8', icon: FiSettings };

// Server's GROUP_LABELS does not always carry a key the data uses (e.g. `ui`).
export const humanizeGroup = (key) =>
  String(key || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// ── Status of a single setting ────────────────────────────────────────────────

export const LOGO_KEY = 'APP_LOGO';

// The question the whole screen exists to answer: is this one set up yet?
// A sensitive key comes back masked rather than raw, but a masked value is
// still a value — blank is blank either way.
export const isUnset = (setting) => !String(setting?.value ?? '').trim();

export const FILTERS = [
  { key: 'all',        label: 'All' },
  { key: 'unset',      label: 'Not set',    hint: 'Keys with no value yet' },
  { key: 'overridden', label: 'Overridden', hint: 'Changed from the .env default' },
  { key: 'sensitive',  label: 'Sensitive',  hint: 'Encrypted at rest, returned masked' },
  { key: 'dirty',      label: 'Unsaved',    hint: 'Edited but not saved' },
];

export const matchesFilter = (setting, filter, isDirty) => {
  switch (filter) {
    case 'unset':      return isUnset(setting);
    case 'overridden': return !!setting.overridden;
    case 'sensitive':  return !!setting.is_sensitive;
    case 'dirty':      return isDirty;
    default:           return true;
  }
};

export const matchesQuery = (setting, query) => {
  if (!query) return true;
  const haystack = `${setting.key} ${setting.label || ''} ${setting.description || ''}`.toLowerCase();
  return haystack.includes(query);
};

// ── Value shape ───────────────────────────────────────────────────────────────

export const prettyJson = (value) => {
  if (!value) return '';
  try { return JSON.stringify(JSON.parse(value), null, 2); } catch { return value; }
};

export const looksLikeJson = (value) => {
  const v = String(value || '').trim();
  if (!v.startsWith('{') && !v.startsWith('[')) return false;
  try { JSON.parse(v); return true; } catch { return false; }
};

// Why a JSON value does not parse, or null. The API reads a broken value back as
// its fallback ({} / []), so a missing quote would silently empty a list.
export const jsonError = (value) => {
  const v = String(value || '').trim();
  if (!v) return null;
  try { JSON.parse(v); return null; } catch (e) { return e.message; }
};

export const isJsonSetting = (setting) =>
  setting.data_type === 'json' || setting.value_type === 'json' || looksLikeJson(setting.value);

// Keys that are filled by a sync button rather than typed into.
export const OPLA_SYNC_KEYS = [
  'OPLA_SALES_PROCESS',
  'OPLA_CUSTOM_FIELDS',
  'OPLA_CONTRACT_TYPE',
  'OPLA_TASK_TYPE',
  'OPLA_RELATED_PARTY_ROLE',
];

// ── Per-group tallies ─────────────────────────────────────────────────────────
// Shared by the sidebar and the narrow-screen group picker so both agree on
// what "3 not set" means. Insertion order follows the API's ordering.
export const buildGroupStats = (data) => {
  const byGroup = {};
  let total = 0;
  let unset = 0;

  data.forEach((setting) => {
    if (setting.key === LOGO_KEY) return;

    const bucket = byGroup[setting.group] || (byGroup[setting.group] = { total: 0, unset: 0, subs: {} });
    const missing = isUnset(setting) ? 1 : 0;

    bucket.total += 1;
    bucket.unset += missing;
    total += 1;
    unset += missing;

    if (setting.sub_group) {
      const sub = bucket.subs[setting.sub_group] || (bucket.subs[setting.sub_group] = { total: 0, unset: 0 });
      sub.total += 1;
      sub.unset += missing;
    }
  });

  return { byGroup, total, unset };
};
